import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Button, Modal } from '@tes/ui';
import { api } from '../../lib/api';
import { runSequential, succeeded, type BulkOutcome } from '../../lib/bulk';
import { messageOf } from '../../lib/errors';
import { dropVariableFromCachedTemplates } from '../../lib/templateCache';
import type { VariableDef } from '../../lib/types';
import { useEditor } from '../../editor/editorStore';
import { isPlaced } from './useVariables';

/**
 * Confirmation for deleting one or many library entries. No typed gate — the
 * server refuses to delete anything still in use, and a definition that isn't
 * placed anywhere is four fields to retype.
 *
 * The "in use" count below is a warning, not the ruling: it comes from draft
 * placements only, while the server also blocks on published version snapshots.
 * Whatever it refuses comes back per-row with its own reason.
 */
export function DeleteVariablesDialog({
  variables,
  onClose,
  onDeleted,
}: {
  variables: VariableDef[];
  onClose: () => void;
  onDeleted: (results: BulkOutcome[]) => void;
}) {
  const qc = useQueryClient();
  const [done, setDone] = useState(0);
  const placed = variables.filter(isPlaced);

  const mutation = useMutation({
    mutationFn: () =>
      runSequential(
        variables,
        (v) => ({ id: v.id, name: v.key }),
        (v) => api.deleteVariable(v.id),
        (n) => setDone(n),
      ),
    onSuccess: (results) => {
      // A variable the server let go had no SAVED placements — but the open
      // editor may still hold unsaved ones, which would fail the next save.
      const editor = useEditor.getState();
      for (const ok of succeeded(results)) {
        editor.removeVariablePlacements(ok.name);
        dropVariableFromCachedTemplates(qc, ok.name);
      }
      onDeleted(results);
    },
  });

  const busy = mutation.isPending;

  return (
    <Modal
      title={`Delete ${variables.length} variable${variables.length === 1 ? '' : 's'}`}
      onClose={onClose}
      footer={
        <>
          {busy && variables.length > 1 && (
            <span className="mr-auto text-sm text-slate-500">
              Deleting {done} of {variables.length}…
            </span>
          )}
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => mutation.mutate()}>
            Delete
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Remove {variables.length === 1 ? 'this definition' : 'these definitions'} from the
          library? Templates that place a variable keep working until you republish them.
        </p>

        <div className="max-h-40 overflow-auto rounded-lg bg-slate-50 p-3">
          <ul className="space-y-0.5 font-mono text-xs text-slate-700">
            {variables.map((v) => (
              <li key={v.id} className="truncate">
                {v.key}
                {isPlaced(v) && (
                  <span className="ml-2 font-sans text-[11px] text-amber-700">
                    placed {v.usage?.placements}×
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {placed.length > 0 && (
          <div className="flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              {placed.length} of these {placed.length === 1 ? 'is' : 'are'} still placed on a
              template and will be refused — remove the placements first.
            </span>
          </div>
        )}

        {mutation.error && (
          <p className="text-sm text-red-600">{messageOf(mutation.error, 'Delete failed')}</p>
        )}
      </div>
    </Modal>
  );
}
